/* WeQuote Dev Board — date helpers. Ported verbatim from app.js. */

export interface DaysLeft { txt: string; cls: string }

export function fmtDate(iso: string): string {
  if (!iso) return ''
  const p = ('' + iso).split('-')
  if (p.length !== 3) return iso
  return p[2] + '/' + p[1] + '/' + p[0].slice(2)
}

export function todayISO(): string {
  const d = new Date()
  const m = d.getMonth() + 1
  const day = d.getDate()
  return d.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day)
}

export function daysLeft(iso: string): DaysLeft {
  if (!iso) return { txt: 'No deadline set', cls: 'grey' }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(iso + 'T00:00:00')
  if (isNaN(d.getTime())) return { txt: 'No deadline set', cls: 'grey' }
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000)
  if (diff > 1) return { txt: diff + ' days left', cls: 'grey' }
  if (diff === 1) return { txt: 'Due tomorrow', cls: 'grey' }
  if (diff === 0) return { txt: 'Due today', cls: 'orange' }
  if (diff === -1) return { txt: 'Due yesterday', cls: 'red' }
  return { txt: Math.abs(diff) + ' days overdue', cls: 'red' }
}

export function daysUntil(iso: string): number | null {
  if (!iso) return null
  const t = new Date()
  t.setHours(0, 0, 0, 0)
  const d = new Date(iso + 'T00:00:00')
  if (isNaN(d.getTime())) return null
  return Math.round((d.getTime() - t.getTime()) / 86400000)
}

export function daysAgo(iso: string): number | null {
  if (!iso) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(iso + 'T00:00:00')
  if (isNaN(d.getTime())) return null
  return Math.round((today.getTime() - d.getTime()) / 86400000)
}

export function daysAgoTxt(n: number): string {
  return n <= 0 ? 'today' : n === 1 ? '1 day ago' : n + ' days ago'
}
export function daysAgoLabel(iso: string): string {
  const n = daysAgo(iso)
  return n === null ? '' : '(' + daysAgoTxt(n) + ')'
}
export function daysLeftLabel(iso: string): string {
  if (!iso) return ''
  return '(' + daysLeft(iso).txt + ')'
}
export function daysAwayLabel(iso: string): string {
  const d = daysUntil(iso)
  if (d === null) return ''
  if (d > 1) return '(' + d + ' days away)'
  if (d === 1) return '(1 day away)'
  if (d === 0) return '(today)'
  if (d === -1) return '(1 day ago)'
  return '(' + Math.abs(d) + ' days ago)'
}
export function deadlineCls(iso: string): string {
  return iso && daysLeft(iso).cls === 'red' ? 'late' : ''
}

export function rankCls(n: number): string {
  return n <= 3 ? 'rank-red' : n <= 8 ? 'rank-orange' : 'rank-grey'
}

/** Human gap between two ISO dates, e.g. "2 months, 5 days". */
export function elapsedLabel(fromISO: string, toISO: string): string {
  if (!fromISO || !toISO) return '—'
  let a = new Date(fromISO + 'T00:00:00')
  let b = new Date(toISO + 'T00:00:00')
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return '—'
  if (b < a) { const t = a; a = b; b = t }
  let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth())
  let days = b.getDate() - a.getDate()
  if (days < 0) {
    months--
    days += new Date(b.getFullYear(), b.getMonth(), 0).getDate()
  }
  if (months <= 0 && days === 0) return 'same day'
  const parts: string[] = []
  if (months > 0) parts.push(months + (months === 1 ? ' month' : ' months'))
  if (days > 0) parts.push(days + (days === 1 ? ' day' : ' days'))
  return parts.join(', ')
}

export function fmtTs(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]
  const hh = ('0' + d.getHours()).slice(-2)
  const mm = ('0' + d.getMinutes()).slice(-2)
  return d.getDate() + ' ' + mon + ' ' + d.getFullYear() + ' · ' + hh + ':' + mm
}

export function cmtSort(a: { ts: string | null }, b: { ts: string | null }): number {
  if (!a.ts && !b.ts) return 0
  if (!a.ts) return -1
  if (!b.ts) return 1
  return a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0
}

/** "Thursday 8th July" — the written-day format (Lee, 3 Sep). */
export function fmtDayName(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso + 'T12:00:00')
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const n = d.getDate()
  const suf = n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'
  return days[d.getDay()] + ' ' + n + suf + ' ' + months[d.getMonth()]
}
